import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * 404 Not Found page.
 *
 * Purpose is technical-SEO first: any unknown URL under the SPA must
 * NOT return a 200 with the homepage content (soft-404). This route
 * renders visible "Not Found" copy AND emits `<meta name="robots"
 * content="noindex, follow">` so crawlers explicitly de-index the URL
 * rather than treating a redirect-to-home as a genuine landing page.
 *
 * We cannot set an HTTP 404 status from a purely client-side SPA, that
 * remains a hosting/edge concern to address separately. Emitting a
 * noindex meta is the correct SPA-layer signal.
 */
export default function NotFoundPage() {
  useEffect(() => {
    document.title = "Page Not Found · PepDirect";

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("robots", "noindex, follow");
    setMeta(
      "description",
      "The page you requested could not be found on PepDirect. Return to the research peptide catalogue or the Research Guide Library.",
    );

    // Remove any canonical set by a previously matched route; a 404
    // should not self-canonicalise.
    document.querySelector('link[rel="canonical"]')?.remove();
  }, []);

  return (
    <div className="container-pd py-24 text-center" data-testid="not-found-page">
      <p className="label-mono text-brandblue">↳ 404</p>
      <h1
        className="mt-4 font-heading text-4xl font-black tracking-tight text-ink sm:text-5xl"
        data-testid="not-found-page-title"
      >
        Page Not Found
      </h1>
      <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-subtle">
        The page you requested is not available. It may have been moved, renamed, or never existed on PepDirect.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/peptides"
          className="btn-pill btn-solid px-6 py-3 text-sm font-semibold"
          data-testid="not-found-cta-catalogue"
        >
          Browse Research Peptides →
        </Link>
        <Link
          to="/learn"
          className="btn-pill btn-outline px-6 py-3 text-sm font-semibold"
          data-testid="not-found-cta-learn"
        >
          Research Guide Library
        </Link>
      </div>
    </div>
  );
}
